import { useCallback, useEffect, useState } from 'react';
import { getStoredTheme, applyTheme } from '../../pages/landing/theme';

/**
 * Tema de la app (claro · automático · oscuro).
 *
 * Comparte almacenamiento con la landing —el mismo módulo, la misma llave
 * `payslip-theme`— a propósito: quien elige oscuro en el sitio público entra a
 * la app en oscuro, sin volver a elegir. Por eso este archivo NO reimplementa
 * nada: reexporta el gestor de la landing y le agrega el hook de React.
 *
 * El modo se escribe en `html[data-theme]`; los tokens de la app viven bajo
 * `.as2` y leen ese atributo (ver el encabezado de app2.css).
 *
 * "auto" sigue al sistema operativo: no se guarda un color, se guarda la
 * INTENCIÓN de seguir al sistema, así que si la persona cambia su tema mientras
 * la app está abierta, la app cambia con él.
 */

export const THEME_MODES = ['light', 'auto', 'dark'];

/** Opciones del selector segmentado (mismo orden que el diseño). */
export const THEME_OPTIONS = [
  { id: 'light', icon: 'light_mode', label: 'Claro' },
  { id: 'auto', icon: 'brightness_auto', label: 'Automático (sigue al sistema)' },
  { id: 'dark', icon: 'dark_mode', label: 'Oscuro' },
];

export { getStoredTheme, applyTheme };

/**
 * Aplica el tema guardado mientras la app está montada y devuelve
 * `[modo, setModo]`.
 */
export function useAppTheme() {
  const [mode, setMode] = useState(() => getStoredTheme());

  // Aplica el modo al montar y cada vez que cambia.
  useEffect(() => {
    applyTheme(mode);
  }, [mode]);

  // En "auto", repinta cuando el sistema cambia de claro a oscuro. El CSS ya
  // reacciona solo (media query), pero esto mantiene sincronizado cualquier
  // cálculo en JS que dependa del modo efectivo.
  useEffect(() => {
    if (mode !== 'auto' || typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = () => applyTheme('auto');
    mq.addEventListener?.('change', onChange);
    return () => mq.removeEventListener?.('change', onChange);
  }, [mode]);

  const setTheme = useCallback((next) => {
    setMode(THEME_MODES.includes(next) ? next : 'auto');
  }, []);

  return [mode, setTheme];
}

/** ¿El modo efectivo es oscuro? (útil para decisiones en JS, no para CSS) */
export function isDarkMode(mode = getStoredTheme()) {
  if (mode === 'dark') return true;
  if (mode === 'light') return false;
  return typeof window !== 'undefined' && window.matchMedia
    ? window.matchMedia('(prefers-color-scheme: dark)').matches
    : true; // el tema base del diseño es oscuro
}
